import moment from "moment";
import type { Consultant } from "@models/Schedule";
import { cn } from "@/lib/utils";
import { getConsultantName } from "./ApplicantList";
import ConsultantPopover from "@components/ConsultantPopover";
import { formatHours, getInitials, WeekScheduleCellVariant, WeekScheduleShift } from "./weekSchedule.utils";

interface Props {
	shift: WeekScheduleShift;
	variant: WeekScheduleCellVariant;
	/** Who works the shift; undefined for unmanned shifts */
	consultant?: Consultant;
	/** Extra hover text after the consultant's name, e.g. who booked the shift */
	title?: string;
	pendingCount?: number;
	invitedCount?: number;
	onOpenMenu: (e: React.MouseEvent) => void;
	onOpenApplicants?: (e: React.MouseEvent) => void;
}

const variantClasses: Record<WeekScheduleCellVariant, string> = {
	booked: "!border-[#49BD79]/50 !bg-[#bdecd1] text-green-950 hover:border-[#49BD79]",
	unmanned: "!border-gray-400 !bg-gray-300 text-gray-800 hover:border-gray-500",
	absent: "!border-[#EB5757]/40 !bg-[#FFEBEB] !text-[#EB5757] hover:border-[#EB5757]",
	declined: "!border-[#EB5757]/40 !bg-[#FFEBEB] !text-[#EB5757] hover:border-[#EB5757]",
	requestDeclined: "!border-[#EB5757]/40 !bg-[#FFEBEB] !text-[#EB5757] hover:border-[#EB5757]",
	draft: "!border-dashed !border-[#219CD0] !bg-[#EAF6FC] text-gray-800 hover:!bg-[#D9EEF8]",
};

/** Initials circle colours when there is no picture */
const avatarClasses: Partial<Record<WeekScheduleCellVariant, string>> = {
	booked: "bg-[#49BD79] text-white",
	absent: "bg-[#EB5757] text-white",
	declined: "bg-[#EB5757] text-white",
};

const getSecondLine = (shift: WeekScheduleShift, variant: WeekScheduleCellVariant) => {
	if (variant === "absent") return "Frånvaro";
	if (variant === "declined") return "Nekat";
	if (variant === "requestDeclined") return "Nekad förfrågan";
	if (variant === "draft") return shift.break ? `Nytt · ${formatHours(shift.break)} h rast` : "Nytt pass";
	if (variant === "unmanned") {
		if (shift.status === "EXTEND_REQUESTED") return "Inväntar svar";
		if (shift.status === "EXTEND_DECLINED") return "Nekat";
		return "Obemannad";
	}
	if (shift.break) return `${formatHours(shift.break)} h rast`;
	return "";
};

const ShiftPill = ({
	shift,
	variant,
	consultant,
	title,
	pendingCount = 0,
	invitedCount = 0,
	onOpenMenu,
	onOpenApplicants,
}: Props) => (
	<div className="relative">
		<button
			type="button"
			onClick={onOpenMenu}
			onContextMenu={(e) => {
				e.preventDefault();
				onOpenMenu(e);
			}}
			className={cn(
				"flex min-h-[52px] w-full cursor-pointer items-center gap-1 !rounded !border !border-solid !py-1 !pr-1 text-left 2xl:gap-1.5",
				consultant ? "!pl-1.5" : "!pl-2",
				variantClasses[variant],
			)}
		>
			{consultant && (
				<ConsultantPopover consultant={consultant}>
					{consultant.profile?.profilePicture ? (
						// Chakra's reset sets img height to auto, so the size has to win over it to stay round
						<img src={consultant.profile.profilePicture} alt="" className="!h-5 !w-5 rounded-full object-cover 2xl:!h-6 2xl:!w-6" />
					) : (
						<span
							className={cn(
								"flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold leading-none 2xl:h-6 2xl:w-6 2xl:text-[10px]",
								avatarClasses[variant],
							)}
						>
							{getInitials(consultant)}
						</span>
					)}
				</ConsultantPopover>
			)}
			{/* The hover text sits on the times, not the button, so it doesn't cover the avatar's card */}
			<span
				className="flex min-w-0 flex-col justify-center"
				title={[consultant && getConsultantName(consultant), title].filter(Boolean).join(" · ") || undefined}
			>
				<span className="whitespace-nowrap text-xs font-semibold leading-tight 2xl:text-[13px]">
					{moment(shift.start).format("HH:mm")}-{moment(shift.end).format("HH:mm")}
				</span>
				<span className="truncate text-xs leading-tight">{getSecondLine(shift, variant)}</span>
			</span>
		</button>
		{(pendingCount > 0 || invitedCount > 0) && (
			<button
				type="button"
				title="Visa ansökningar"
				onClick={onOpenApplicants}
				// Chakra's reset sets overflow on buttons, so the clip has to win over it for the corners to stay round
				className="absolute -right-1 -top-1.5 flex cursor-pointer items-stretch !overflow-hidden !rounded-full !border !border-solid !border-gray-400 !bg-white"
			>
				{pendingCount > 0 && (
					<span className="flex h-4 min-w-4 items-center justify-center bg-[#219CD0] px-1 text-[10px] font-bold leading-none text-white">
						{pendingCount}
					</span>
				)}
				{invitedCount > 0 && (
					<span className="flex h-4 min-w-4 items-center justify-center px-1 text-[10px] font-bold leading-none text-gray-700">
						{invitedCount}
					</span>
				)}
			</button>
		)}
	</div>
);

export default ShiftPill;
